import {
  Form,
  Loading,
  SaveButton,
  useGetOne,
  useInput,
  useNotify,
  useRedirect,
  useTranslate
} from 'react-admin'
import { useFormState, useFormContext } from 'react-hook-form'
import React, { useState } from 'react'
import {
  Box,
  Button,
  Grid,
  Input,
  Typography,
  useMediaQuery,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl
} from '@mui/material'
import log from 'loglevel'
import { isNil, map, toNumber } from 'lodash/fp'
import axios from 'axios'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import ReactMarkdown from 'react-markdown'
import Countdown from 'react-countdown'

log.setLevel(log.levels.DEBUG)

type SelectItem = {
  label: string
  value: string
}
type CodingAssesment = {
  name: string
  header: string
  questions: {
    question: string
  }
  start: string
  duration: number
}

const env = process.env['REACT_APP_ENV']
const urls = !isNil(process.env['REACT_APP_APOLLO'])
  ? {
      prd: 'https://api-b2b-v2-v2.terashite.jp/partner-user-public/v1/codingAssesments',
      stg: 'https://api-b2b-v2-v2-stg.terashite.jp/partner-user-public/v1/codingAssesments',
      int: 'https://api-b2b-v2-v2-int.dev.terashite.jp/partner-user-public/v1/codingAssesments',
      dev: 'https://api-b2b-v2-v2.dev.terashite.jp/partner-user-public/v1/codingAssesments',
      exp: 'https://api-b2b-v2-v2-exp.dev.terashite.jp/partner-user-public/v1/codingAssesments'
    }
  : {
      prd: 'https://api-b2b-v2.terashite.jp/partner-user-public/v1/codingAssesments',
      stg: 'https://api-b2b-v2-stg.terashite.jp/partner-user-public/v1/codingAssesments',
      int: 'https://api-b2b-v2-int.dev.terashite.jp/partner-user-public/v1/codingAssesments',
      dev: 'https://api-b2b-v2.dev.terashite.jp/partner-user-public/v1/codingAssesments',
      exp: 'https://api-b2b-v2-exp.dev.terashite.jp/partner-user-public/v1/codingAssesments'
    }
const url = urls[env as keyof typeof urls]
const schemaCommon = {
  codes: z.array(z.string().min(1, { message: 'required' }))
}
const schema = z.object(schemaCommon)

const SubmitButton: React.FC<{ id: string }> = ({ id, ...props }) => {
  const [enable, setEnable] = useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid } = useFormState()
  const translate = useTranslate()
  const redirect = useRedirect()
  const onSubmit = (): void => {
    const { ...data } = getValues()
    const input = {
      id,
      ...data
    }
    setEnable(false)
    log.debug({ input })
    axios({
      url: `${url}/${id}`,
      method: 'POST',
      data: input
    })
      .then(() => {
        redirect(`/codingAssesment/${id}/complete`)
      })
      .catch(log.error)
  }
  React.useEffect(() => {
    log.debug('[SubmitButton]', { errors, isValid, data: getValues() })
    setEnable(isValid)
  }, [errors, isValid, getValues])
  return (
    <SaveButton
      {...props}
      icon={<></>}
      variant="contained"
      size="large"
      disabled={!enable}
      sx={{
        borderRadius: '40px',
        backgroundColor: '#4472c4'
      }}
      label={translate('resources.codingAssesment.submit.l1')}
      onClick={onSubmit}
    />
  )
}
const StartButton: React.FC<{ id: string; start: string }> = ({
  id,
  start,
  ...props
}) => {
  const translate = useTranslate()
  const [enable, setEanble] = useState(true)
  const redirect = useRedirect()
  const notify = useNotify()
  const onClick = (): void => {
    notify('resources.codingAssesment.loading')
    log.debug({ start })
    setEanble(false)
    if (isNil(start)) {
      const input = {
        id,
        start: Date.now().toString()
      }
      axios({
        url: `${url}/${id}`,
        method: 'PUT',
        data: input
      })
        .then(() => {
          redirect(`/codingAssesment/${id}/start`)
        })
        .catch(log.error)
    } else {
      redirect(`/codingAssesment/${id}/start`)
    }
  }
  return (
    <Button
      {...props}
      variant="contained"
      size="large"
      sx={{
        borderRadius: '40px',
        backgroundColor: '#4472c4'
      }}
      disabled={!enable}
      onClick={onClick}>
      {translate('resources.codingAssesment.start')}
    </Button>
  )
}
const FormContent: React.FC<{ id: string }> = ({ id, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { data, isLoading } = useGetOne('codingAssesments', { id })
  if (isLoading) return <Loading />
  if (isNil(data)) return <></>
  const { name, header, start } = data
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ backgroundColor: '#fff7e1' }}>
          <Box sx={{ marginLeft: '0.5em', paddingTop: '1em' }}>
            <picture>
              <img src="/logo.png" width="100px" alt="terashite R" />
            </picture>
          </Box>
          <Box
            sx={{
              marginLeft: isDesktop ? '2em' : '0.5em',
              marginRight: '0.5em'
            }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant={isDesktop ? 'h4' : 'h6'}
                  sx={{
                    marginTop: '1.5em'
                  }}>
                  {translate('resources.codingAssesment.title')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              <Grid item xs={12}>
                <ReactMarkdown>{header}</ReactMarkdown>
                <p />
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <TextField source="name" value={name} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1.5em',
                  marginBottom: '2.5em'
                }}>
                <StartButton {...props} id={id} start={start} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
const StartFormContent: React.FC<{ id: string }> = ({ id, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const redirect = useRedirect()
  const { data, isLoading } = useGetOne('codingAssesments', { id })
  if (isLoading) return <Loading />
  if (isNil(data)) return <></>
  log.debug({ data })
  const { questions, start, duration } = data as CodingAssesment
  const renderer: React.FC<any> = ({ minutes, seconds, completed }) => {
    if (completed as boolean) {
      // Render a completed state
      return <></>
    } else {
      // Render a countdown
      return (
        <Typography variant="h6" color="#ff0000">
          残り時間 {minutes}分{seconds}秒
        </Typography>
      )
    }
  }
  const onComplete = (): void => {
    redirect(`/codingAssesment/${id}/complete`)
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ backgroundColor: '#fff7e1' }}>
          <Box sx={{ marginLeft: '0.5em', paddingTop: '1em' }}>
            <picture>
              <img src="/logo.png" width="100px" alt="terashite R" />
            </picture>
          </Box>
          <Box
            sx={{
              marginLeft: isDesktop ? '2em' : '0.5em',
              marginRight: '0.5em'
            }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant={isDesktop ? 'h4' : 'h6'}
                  sx={{
                    marginTop: '1.5em'
                  }}>
                  {translate('resources.codingAssesment.title')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              {
                // @ts-expect-error
                map.convert({ cap: false })(
                  (question: string, index: number) => {
                    return (
                      <Grid item xs={12} key={index}>
                        <ReactMarkdown>{question}</ReactMarkdown>
                        <p />
                        {!isNil(start) && (
                          <Countdown
                            date={toNumber(start) + duration * 1000}
                            renderer={renderer}
                            onComplete={onComplete}
                          />
                        )}
                        <TextInput
                          source={`codes.${index}`}
                          placeholder={translate(
                            'resources.codingAssesment.input'
                          )}
                          multiline
                        />
                      </Grid>
                    )
                  }
                )(questions)
              }
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1.5em',
                  marginBottom: '2.5em'
                }}>
                <SubmitButton {...props} id={id} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const TextInput: React.FC<{
  source: string
  placeholder: string
  validate?: any
  multiline?: boolean
}> = ({ source, placeholder, validate, multiline, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, validate, ...props })
  // const translate = useTranslate()
  // const label = translate(`resources.codingAssesment.fields.${field.name}`)
  // const { errors } = useFormState()
  const multilineValue = isNil(multiline) ? false : multiline
  return (
    <>
      {/* <Typography variant="subtitle1">{label}</Typography> */}
      <Box
        {...props}
        border={1}
        sx={{
          minHeight: '3em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em',
          marginRight: isDesktop ? '2em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Input
          {...field}
          placeholder={placeholder}
          fullWidth
          disableUnderline
          minRows={3}
          multiline={multilineValue}
          sx={{ marginLeft: '1em', marginRight: '1em', fontSize: '18px' }}
          required={isRequired}
        />
      </Box>
      {/* {!isNil(errors[source]?.message) && <ValidateError source={source} />} */}
    </>
  )
}
export const TextField: React.FC<{ source: string; value: string }> = ({
  source,
  value,
  ...props
}) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const label = translate(`resources.codingAssesment.fields.${source}`)
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        sx={{
          height: '3em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em'
        }}>
        <Typography variant="h6">{value}</Typography>
      </Box>
    </>
  )
}
export const SelectInput: React.FC<{
  source: string
  choices: SelectItem[]
}> = ({ source, choices, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, ...props })
  const translate = useTranslate()
  const label = translate(`resources.codingAssesment.fields.${field.name}`)
  const { errors } = useFormState()
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        sx={{
          minHeight: '3em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em'
        }}>
        <FormControl required={isRequired} fullWidth>
          <RadioGroup
            {...field}
            row={isDesktop}
            sx={{ marginLeft: '1em', marginRight: '1em', fontSize: '20px' }}>
            {map((item: SelectItem) => (
              <FormControlLabel
                value={item.value}
                control={<Radio />}
                label={<Typography variant="h5">{item.label}</Typography>}
              />
            ))(choices)}
          </RadioGroup>
        </FormControl>
      </Box>
      {!isNil(errors[source]?.message) && <ValidateError source={source} />}
    </>
  )
}
export const ValidateError: React.FC<{ source: string }> = ({ source }) => {
  const translate = useTranslate()
  const { errors } = useFormState()
  return (
    <Typography variant="subtitle2" color="red" sx={{ marginLeft: '1em' }}>
      {translate(errors[source]?.message as string)}
    </Typography>
  )
}
export const CodingAssesmentForm: React.FC<{ id: string }> = ({
  id,
  ...props
}) => {
  return (
    <Form resolver={zodResolver(schema)} mode="onBlur" reValidateMode="onBlur">
      <FormContent {...props} id={id} />
    </Form>
  )
}
export const CodingAssesmentStartForm: React.FC<{ id: string }> = ({
  id,
  ...props
}) => {
  return (
    <Form resolver={zodResolver(schema)} mode="onBlur" reValidateMode="onBlur">
      <StartFormContent {...props} id={id} />
    </Form>
  )
}
export const CodingAssesmentCompleteForm: React.FC = () => {
  const translate = useTranslate()
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box
          sx={{
            backgroundColor: '#fff7e1',
            paddingBottom: '3em'
          }}>
          <Box sx={{ marginLeft: '0.5em', paddingTop: '1em' }}>
            <picture>
              <img src="/logo.png" width="100px" alt="terashite R" />
            </picture>
          </Box>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '0em'
                  }}>
                  {translate('resources.codingAssesment.response')}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
